import { FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useChurch } from '../hooks/useChurches'
import { useUpdateChurch, useArchiveChurch, useCheckSlugAvailable, useUpdateChurchSlug, useSubscriptionStatus } from '../hooks/useChurchAdmin'
import { ApiError } from '../lib/api'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn, FormField, TextInput, AdminTextArea } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import { ImageUpload } from '../components/admin/ImageUpload'
import LocationPicker from '../components/LocationPicker'
import { getDenominationConfig } from '@fellowship/shared'
import type { ServiceTime } from '@fellowship/shared'

export default function ChurchEditPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { data: churchData, isLoading } = useChurch(slug)
  const church = churchData?.data
  const denomConfig = getDenominationConfig(church?.denomination)
  const updateChurch = useUpdateChurch(church?.id ?? '')
  const archiveChurch = useArchiveChurch(church?.id ?? '')
  const checkSlug = useCheckSlugAvailable()
  const updateSlug = useUpdateChurchSlug(church?.id ?? '', slug ?? '')
  const { data: subData } = useSubscriptionStatus(church?.id)
  const subscriptionActive = subData?.data?.status === 'active' || subData?.data?.status === 'trialing'

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [coverSaved, setCoverSaved] = useState(false)
  const [logoSaved, setLogoSaved] = useState(false)

  // Slug editor state
  const [slugInput, setSlugInput] = useState('')
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'current'>('idle')
  const [slugSaved, setSlugSaved] = useState(false)
  const slugDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [tagline, setTagline] = useState('')
  const [email, setEmail] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [phone, setPhone] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [city, setCity] = useState('')
  const [stateFld, setStateFld] = useState('')
  const [zip, setZip] = useState('')
  const [locationLat, setLocationLat] = useState<number | null>(null)
  const [locationLng, setLocationLng] = useState<number | null>(null)
  const [mapAddressText, setMapAddressText] = useState('')
  const [serviceTimes, setServiceTimes] = useState<ServiceTime[]>([])
  const [givingUrl, setGivingUrl] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!church) return
    setSlugInput(church.slug)
    setName(church.name)
    setDescription(church.description ?? '')
    setTagline(church.tagline ?? '')
    setEmail(church.email ?? '')
    setWebsiteUrl(church.websiteUrl ?? '')
    setPhone(church.phone ?? '')
    setAddressLine1(church.addressLine1 ?? '')
    setCity(church.city ?? '')
    setStateFld(church.state ?? '')
    setZip(church.zip ?? '')
    setLocationLat(church.locationLat)
    setLocationLng(church.locationLng)
    setServiceTimes(church.serviceTimes ?? [])
    setGivingUrl(church.givingUrl ?? '')
    const parts = [church.addressLine1, church.city, church.state, church.zip].filter(Boolean)
    setMapAddressText(parts.join(', '))
  }, [church])

  function addServiceTime() {
    setServiceTimes((prev) => [...prev, { name: '', day: '', time: '' }])
  }

  function updateServiceTime(index: number, field: keyof ServiceTime, value: string) {
    setServiceTimes((prev) => prev.map((st, i) => i === index ? { ...st, [field]: value } : st))
  }

  function removeServiceTime(index: number) {
    setServiceTimes((prev) => prev.filter((_, i) => i !== index))
  }

  function handleSlugChange(value: string) {
    const v = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
    setSlugInput(v)
    setSlugSaved(false)
    if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current)
    if (v === church?.slug) { setSlugStatus('current'); return }
    if (!SLUG_RE.test(v)) { setSlugStatus(v.length > 0 ? 'invalid' : 'idle'); return }
    setSlugStatus('checking')
    slugDebounceRef.current = setTimeout(() => {
      checkSlug.mutate(v, {
        onSuccess: (res) => setSlugStatus(res.data.available ? 'available' : 'taken'),
        onError: () => setSlugStatus('idle'),
      })
    }, 400)
  }

  function handleSlugSave() {
    if (slugStatus !== 'available') return
    updateSlug.mutate(slugInput, {
      onSuccess: (res) => {
        setSlugStatus('current')
        setSlugSaved(true)
        navigate(`/churches/${res.data.slug}/edit`, { replace: true })
      },
    })
  }

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!name.trim()) errs.name = 'Church name is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return
    updateChurch.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        tagline: tagline.trim() || null,
        websiteUrl: websiteUrl.trim() || null,
        phone: phone.trim() || null,
        addressLine1: addressLine1.trim() || null,
        city: city.trim() || null,
        state: stateFld.trim() || null,
        zip: zip.trim() || null,
        email: email.trim() || null,
        serviceTimes: serviceTimes.filter((st) => st.name.trim() || st.day.trim() || st.time.trim()),
        givingUrl: givingUrl.trim() || null,
        ...(locationLat !== null && locationLng !== null ? { locationLat, locationLng } : {}),
      },
      { onSuccess: () => navigate(`/churches/${slug}`) },
    )
  }

  const serverError = updateChurch.error instanceof ApiError ? updateChurch.error.message : null

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Loading…</div>
      </div>
    )
  }

  return (
    <AdminShell>
      <form onSubmit={handleSubmit} noValidate>
        <AdminPageHeader
          breadcrumb={['Church profile', 'Edit']}
          eyebrow="Public-facing"
          title="Edit your"
          titleItalic="church profile."
          sub="This is what visitors see on your public page."
          actions={
            <>
              <Link to={`/churches/${slug}`} className="no-underline">
                <AdmBtn variant="ghost" icon="arrow">View public page</AdmBtn>
              </Link>
              <AdmBtn type="submit" variant="primary" disabled={updateChurch.isPending}>
                {updateChurch.isPending ? 'Saving…' : 'Save changes'}
              </AdmBtn>
            </>
          }
        />

        {serverError && (
          <div role="alert" className="px-4 py-3 rounded-xl mb-5 bg-danger-soft border border-danger-line text-danger-deep text-[13px]">
            {serverError}
          </div>
        )}

        <div className="grid gap-5" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
          <div className="flex flex-col gap-5">
            <AdminCard title="Cover photo" padding={14}>
              <div
                className="h-[200px] rounded-xl relative overflow-hidden border border-line-soft"
                style={{
                  background: church?.coverImageUrl
                    ? 'transparent'
                    : 'repeating-linear-gradient(135deg, var(--primary-soft) 0 18px, var(--surface-2) 18px 38px)',
                }}
              >
                {church?.coverImageUrl
                  ? <img src={church.coverImageUrl} alt="" className="w-full h-full object-cover" />
                  : (
                    <div className="absolute bottom-3 left-3.5 bg-white/90 px-2.5 py-1 rounded-md text-[10px] text-muted font-mono tracking-wide">
                      NO COVER PHOTO
                    </div>
                  )
                }
                <div className="absolute top-3 right-3 flex gap-1.5">
                  <ImageUpload
                    context="church-cover"
                    onUpload={(url) => {
                      updateChurch.mutate({ coverImageUrl: url }, {
                        onSuccess: () => { setCoverSaved(true); setTimeout(() => setCoverSaved(false), 2000) },
                      })
                    }}
                  >
                    <AdmBtn variant="ghost" size="sm" icon="upload" style={{ background: 'rgba(255,255,255,0.95)' }}>Replace</AdmBtn>
                  </ImageUpload>
                  {coverSaved && (
                    <span className="text-[11px] text-success-deep bg-white/90 px-2 py-0.5 rounded-md">Saved</span>
                  )}
                </div>
              </div>
              <div className="mt-2.5 text-[11px] text-muted">
                Used as the banner on your public page. Wide crops work best — minimum 1600×800.
              </div>
            </AdminCard>

            <AdminCard title="Identity">
              <div className="flex gap-5">
                <div className="shrink-0">
                  <div className="eyebrow mb-2">Logo</div>
                  <div className="w-[100px] h-[100px] rounded-2xl bg-primary-soft text-primary-deep border border-line-soft flex items-center justify-center font-serif text-[44px] font-medium overflow-hidden">
                    {church?.logoUrl
                      ? <img src={church.logoUrl} alt="" className="w-full h-full object-cover" />
                      : (church?.name.slice(0, 1) ?? '?')
                    }
                  </div>
                  <div className="mt-2">
                    <ImageUpload
                      context="church-logo"
                      onUpload={(url) => {
                        updateChurch.mutate({ logoUrl: url }, {
                          onSuccess: () => { setLogoSaved(true); setTimeout(() => setLogoSaved(false), 2000) },
                        })
                      }}
                    >
                      <AdmBtn variant="ghost" size="sm" icon="upload">Upload</AdmBtn>
                    </ImageUpload>
                    {logoSaved && (
                      <span className="text-[11px] text-success-deep">Saved</span>
                    )}
                  </div>
                </div>

                <div className="flex-1 flex flex-col gap-3.5">
                  <FormField label="Church name" required htmlFor="name" error={errors.name}>
                    <TextInput
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      aria-invalid={Boolean(errors.name)}
                    />
                  </FormField>
                  <FormField label="Denomination">
                    <TextInput value={church?.denomination ?? ''} readOnly style={{ opacity: 0.6 }} />
                  </FormField>
                </div>
              </div>
            </AdminCard>

            <AdminCard title="About">
              <div className="flex flex-col gap-3.5">
                <FormField
                  label="Short tagline"
                  hint="One line — shown under your name on the public page."
                  htmlFor="tagline"
                >
                  <TextInput
                    id="tagline"
                    type="text"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Weekly fellowship · hikes · small groups"
                  />
                </FormField>
                <FormField
                  label="Description"
                  hint="Up to 600 characters — shown on your public page."
                  htmlFor="description"
                >
                  <AdminTextArea
                    id="description"
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </FormField>
              </div>
            </AdminCard>
          </div>

          <div className="flex flex-col gap-5">
            <AdminCard title="Location">
              <div className="flex flex-col gap-3.5">
                <FormField label="Street address" required htmlFor="addressLine1">
                  <TextInput
                    id="addressLine1"
                    type="text"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    icon="pin"
                  />
                </FormField>
                <div className="grid gap-2.5" style={{ gridTemplateColumns: '1fr 56px 72px' }}>
                  <FormField label="City" htmlFor="city">
                    <input
                      id="city"
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="input"
                    />
                  </FormField>
                  <FormField label="State" htmlFor="state">
                    <input
                      id="state"
                      type="text"
                      value={stateFld}
                      onChange={(e) => setStateFld(e.target.value)}
                      maxLength={2}
                      className="input"
                    />
                  </FormField>
                  <FormField label="ZIP" htmlFor="zip">
                    <input
                      id="zip"
                      type="text"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      className="input"
                    />
                  </FormField>
                </div>

                <LocationPicker
                  addressText={mapAddressText}
                  onAddressChange={setMapAddressText}
                  lat={locationLat}
                  lng={locationLng}
                  onCoordinatesChange={(lat, lng) => { setLocationLat(lat); setLocationLng(lng) }}
                  showModeToggle={false}
                  mode="venue"
                  placeholder="Search to reposition pin…"
                />
              </div>
            </AdminCard>

            <AdminCard title="Contact">
              <div className="flex flex-col gap-3.5">
                <FormField label="Public email" htmlFor="contactEmail">
                  <TextInput
                    id="contactEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@yourchurch.org"
                    icon="mail"
                  />
                </FormField>
                <FormField label="Phone" htmlFor="phone">
                  <TextInput
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 000-0000"
                  />
                </FormField>
                <FormField label="Website" htmlFor="websiteUrl">
                  <TextInput
                    id="websiteUrl"
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://yourchurch.org"
                  />
                </FormField>
                <FormField label="Online giving URL" htmlFor="givingUrl" hint="Linked on your bulletin and public page.">
                  <TextInput
                    id="givingUrl"
                    type="url"
                    value={givingUrl}
                    onChange={(e) => setGivingUrl(e.target.value)}
                    placeholder="https://yourchurch.org/give"
                  />
                </FormField>
              </div>
            </AdminCard>

            <AdminCard title="Service times" action={
              <button
                type="button"
                onClick={addServiceTime}
                className="flex items-center gap-1 text-[12px] text-primary font-semibold bg-transparent border-0 cursor-pointer"
              >
                <AdmIcn name="plus" size={13} />
                Add
              </button>
            }>
              {serviceTimes.length === 0 ? (
                <div className="text-[12.5px] text-muted text-center py-2">
                  No service times yet — click Add to get started.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 80px 28px', gap: 8, padding: '0 2px' }}>
                    <span className="text-[10.5px] font-semibold text-muted uppercase tracking-wide">Name</span>
                    <span className="text-[10.5px] font-semibold text-muted uppercase tracking-wide">Day</span>
                    <span className="text-[10.5px] font-semibold text-muted uppercase tracking-wide">Time</span>
                    <span />
                  </div>
                  {serviceTimes.map((st, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 80px 28px', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={st.name}
                        onChange={(e) => updateServiceTime(i, 'name', e.target.value)}
                        placeholder={`e.g. ${denomConfig.eventNameSuggestions.study?.[0] ?? 'Bible Study'}`}
                        className="input text-[12.5px]"
                      />
                      <select
                        value={st.day}
                        onChange={(e) => updateServiceTime(i, 'day', e.target.value)}
                        className="input text-[12.5px]"
                        style={{ width: '100%' }}
                      >
                        <option value="">Day</option>
                        {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={st.time}
                        onChange={(e) => updateServiceTime(i, 'time', e.target.value)}
                        placeholder="9:00 AM"
                        className="input text-[12.5px]"
                        style={{ width: '100%' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeServiceTime(i)}
                        className="w-[28px] h-[28px] shrink-0 flex items-center justify-center rounded-lg border border-line-soft bg-surface text-muted hover:text-danger cursor-pointer"
                      >
                        <AdmIcn name="x" size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </AdminCard>

            <AdminCard title="Your page URL">
              {!subscriptionActive ? (
                <div className="p-3.5 rounded-xl bg-s2 border border-line-soft text-center">
                  <div className="text-[13px] font-semibold text-ink mb-1">Subscription required</div>
                  <div className="text-[12px] text-muted mb-3">Customize your church's URL with an active plan.</div>
                  <Link to={`/churches/${slug}/billing`} className="no-underline">
                    <AdmBtn variant="primary" size="sm">View plans</AdmBtn>
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="text-[12px] text-muted px-1">
                    Current URL:{' '}
                    <span className="font-medium text-ink">fellowship-app.com/churches/{church?.slug}</span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="flex items-center flex-1 border border-line rounded-xl overflow-hidden bg-surface focus-within:border-primary transition-colors">
                      <span className="px-3 text-[12px] text-muted shrink-0 select-none border-r border-line py-2.5">
                        fellowship-app.com/churches/
                      </span>
                      <input
                        className="flex-1 px-3 py-2.5 text-[13px] font-medium bg-transparent outline-none text-ink min-w-0"
                        value={slugInput}
                        onChange={(e) => handleSlugChange(e.target.value)}
                        placeholder={church?.slug}
                        maxLength={50}
                        spellCheck={false}
                        autoComplete="off"
                      />
                    </div>
                    <AdmBtn
                      variant="primary"
                      size="sm"
                      disabled={slugStatus !== 'available' || updateSlug.isPending}
                      onClick={handleSlugSave}
                    >
                      {updateSlug.isPending ? 'Saving…' : 'Save'}
                    </AdmBtn>
                  </div>
                  <div className="text-[11.5px] px-1">
                    {slugStatus === 'checking' && <span className="text-muted">Checking…</span>}
                    {slugStatus === 'available' && <span className="text-good">✓ Available</span>}
                    {slugStatus === 'taken' && <span className="text-danger">✗ Already taken</span>}
                    {slugStatus === 'invalid' && <span className="text-warn">Use 3–50 lowercase letters, numbers, or hyphens. No leading/trailing hyphens.</span>}
                    {slugStatus === 'current' && !slugSaved && <span className="text-muted">This is your current URL</span>}
                    {slugSaved && <span className="text-good">✓ URL updated</span>}
                  </div>
                </div>
              )}
            </AdminCard>

            <AdminCard title="Danger zone">
              <div className="p-3.5 rounded-xl bg-[#FAEBE7] border border-[#E9C8C0] flex items-center gap-3.5">
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-[#7A2A1E]">Archive church</div>
                  <div className="text-[11px] text-[#7A2A1E] opacity-80 mt-0.5">
                    Hides your profile and stops all reminders. Past events stay accessible.
                  </div>
                </div>
                {showArchiveConfirm ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <AdmBtn
                      variant="danger"
                      size="sm"
                      disabled={archiveChurch.isPending}
                      onClick={() => archiveChurch.mutate(undefined, { onSuccess: () => navigate('/') })}
                    >
                      {archiveChurch.isPending ? 'Archiving…' : 'Confirm'}
                    </AdmBtn>
                    <AdmBtn variant="ghost" size="sm" onClick={() => setShowArchiveConfirm(false)}>Cancel</AdmBtn>
                  </div>
                ) : (
                  <AdmBtn variant="danger" size="sm" onClick={() => setShowArchiveConfirm(true)}>Archive</AdmBtn>
                )}
              </div>
            </AdminCard>
          </div>
        </div>

        <div className="sticky bottom-4 z-20 mt-6 px-[18px] py-3.5 bg-surface border border-line-soft rounded-2xl flex items-center justify-end gap-2.5 shadow-[0_8px_24px_rgba(31,26,46,0.12)]">
          <Link to={`/churches/${slug}/dashboard`} className="no-underline">
            <AdmBtn variant="ghost">Cancel</AdmBtn>
          </Link>
          <AdmBtn type="submit" variant="primary" disabled={updateChurch.isPending}>
            {updateChurch.isPending ? 'Saving…' : 'Save changes'}
          </AdmBtn>
        </div>
      </form>
    </AdminShell>
  )
}
